import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type DealCompetitorsResult,
  type ListCompetitorsResult,
} from '@whinerp/client';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';

/**
 * Who else is in this deal, and how it went against each (G-085).
 *
 * The outcome is per COMPETITOR, not per deal. A deal lost to one competitor
 * while another dropped out is the case a single field on the deal cannot
 * express, and it is exactly the case a competitive report exists to find - so
 * the panel is a list of rows with their own state rather than one picker.
 *
 * Names come from a workspace list rather than a free-text box. "Acme", "ACME
 * Ltd" and "acme" are three competitors in a report and one in the market, and
 * a rep typing under time pressure produces all three.
 */

const OUTCOMES = ['unknown', 'won-against', 'lost-to', 'withdrew'] as const;

export function CompetitorsPanel({ dealId }: { dealId: string }): JSX.Element {
  const t = useT();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState('');

  const onDeal = useQuery<DealCompetitorsResult, ApiError>({
    queryKey: ['deal-competitors', dealId],
    queryFn: () => api.dealCompetitors({ path: { dealId } }),
  });
  const all = useQuery<ListCompetitorsResult, ApiError>({
    queryKey: ['competitors'],
    queryFn: () => api.listCompetitors({}),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['deal-competitors', dealId] });
    void queryClient.invalidateQueries({ queryKey: ['competitors'] });
  };

  const set = useMutation<unknown, ApiError, { competitorId: string; outcome: string }>({
    mutationFn: (input) =>
      api.setDealCompetitor({
        path: { dealId, competitorId: input.competitorId },
        body: { outcome: input.outcome as 'unknown' },
      }),
    onSuccess: refresh,
  });
  const remove = useMutation<unknown, ApiError, string>({
    mutationFn: (competitorId) =>
      api.removeDealCompetitor({ path: { dealId, competitorId } }),
    onSuccess: refresh,
  });
  const name = useMutation<{ competitorId: string }, ApiError, string>({
    mutationFn: (value) => api.saveCompetitor({ body: { name: value } }),
    onSuccess: (created) => {
      setAdding('');
      set.mutate({ competitorId: created.competitorId, outcome: 'unknown' });
    },
  });

  const failure = set.error ?? remove.error ?? name.error;
  const already = new Set((onDeal.data?.competitors ?? []).map((entry) => entry.competitorId));
  const offerable = (all.data?.competitors ?? []).filter(
    (competitor) => competitor.active && !already.has(competitor.competitorId),
  );

  return (
    <Panel data-region="competitors">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('deal.competitors')}</SectionLabel>

        {failure ? (
          <p data-competitor-refusal className="text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        {onDeal.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : (onDeal.data?.competitors.length ?? 0) === 0 ? (
          <p className="text-meta text-ink-meta">{t('deal.competitors.none')}</p>
        ) : (
          <ul className="flex flex-col">
            {onDeal.data!.competitors.map((entry) => (
              <li
                key={entry.competitorId}
                data-deal-competitor={entry.competitorId}
                className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
              >
                <span className="min-w-0 flex-1 truncate text-body text-ink">{entry.name}</span>
                <label className="flex items-center gap-1">
                  <span className="sr-only">
                    {t('deal.competitors.outcomeFor', { values: { name: entry.name } })}
                  </span>
                  <select
                    data-competitor-outcome={entry.competitorId}
                    value={entry.outcome}
                    disabled={set.isPending}
                    onChange={(event) =>
                      set.mutate({ competitorId: entry.competitorId, outcome: event.target.value })
                    }
                    className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
                  >
                    {OUTCOMES.map((outcome) => (
                      <option key={outcome} value={outcome}>
                        {t(`deal.competitors.${outcome}` as 'deal.competitors.unknown')}
                      </option>
                    ))}
                  </select>
                </label>
                <Button
                  intent="ghost"
                  size="small"
                  data-competitor-remove={entry.competitorId}
                  onClick={() => remove.mutate(entry.competitorId)}
                >
                  {t('action.remove')}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-2">
          {/*
            The workspace list first, a new name second.

            A picker of names already in use is how the list stays one list.
            The free-text box is there because the first deal against a new
            competitor should not require a trip to Setup.
          */}
          {offerable.length > 0 ? (
            <label className="flex items-center gap-1">
              <span className="sr-only">{t('deal.competitors.add')}</span>
              <select
                data-competitor-add
                value=""
                onChange={(event) => {
                  if (event.target.value !== '') {
                    set.mutate({ competitorId: event.target.value, outcome: 'unknown' });
                  }
                }}
                className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
              >
                <option value="">{t('deal.competitors.add')}</option>
                {offerable.map((competitor) => (
                  <option key={competitor.competitorId} value={competitor.competitorId}>
                    {competitor.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="flex items-center gap-1">
            <span className="sr-only">{t('deal.competitors.newName')}</span>
            <input
              data-competitor-new
              value={adding}
              placeholder={t('deal.competitors.newName')}
              onChange={(event) => setAdding(event.currentTarget.value)}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            />
          </label>
          <Button
            intent="secondary"
            size="small"
            data-competitor-create
            busy={name.isPending}
            onClick={() => {
              if (adding.trim() !== '') name.mutate(adding.trim());
            }}
          >
            {t('action.add')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
